import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebase/config";
import { FaLock, FaUser, FaEye, FaEyeSlash } from "react-icons/fa";
import BanatLogo from "../assets/banat1.webp";
import "./AdminLogin.css";
import { setPageMeta } from "../utils/seo";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setPageMeta({
      title: "Admin Login | Banat India",
      description:
        "Administrator sign in for Banat India management tools.",
      keywords: "admin login, Banat India admin, management portal",
    });
  }, []);

  // Admin credentials (in production, use environment variables)
  const ADMIN_EMAILS = [
    "admin@banatindia.com",
    "support@banatindia.com",
    "manager@banatindia.com"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Check if email is in admin list
      if (!ADMIN_EMAILS.includes(email.toLowerCase())) {
        setError("Access denied. Only authorized administrators can access this area.");
        setLoading(false);
        return;
      }

      // Authenticate with Firebase for real admin accounts
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Check if user is admin (you can add custom claims in Firebase)
      if (userCredential.user) {
        // Store admin session
        localStorage.setItem("adminSession", "true");
        localStorage.setItem("adminEmail", email);

        // Redirect to admin dashboard
        navigate("/admin-dashboard");
      }
    } catch (error) {
      
      if (error.code === "auth/user-not-found") {
        setError("Admin account not found. Please contact system administrator.");
      } else if (error.code === "auth/wrong-password") {
        setError("Incorrect password. Please try again.");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many failed attempts. Please try again later.");
      } else {
        setError("Login failed. Please check your credentials and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-card">
        <div className="admin-login-header">
          <div className="admin-logo-image-container">
            <img src={BanatLogo} alt="Banat India Admin" className="admin-logo-image" />
          </div>
          <h1>Admin Access</h1>
          <p>Enter your administrator credentials</p>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form">
          {error && (
            <div className="error-message">
              <FaUser />
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Admin Email</label>
            <div className="input-wrapper">
              <FaUser className="input-icon" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@banatindia.com"
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="input-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={loading}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="admin-login-btn"
            disabled={loading}
          >
            {loading ? "Signing In..." : "Sign In to Admin Panel"}
          </button>

          {/* <div className="admin-auth-link">
            <button
              type="button"
              onClick={() => navigate("/admin-register")}
            >
              Need to create admin account? Register here
            </button>
          </div> */}
        </form>

        <div className="admin-login-footer">
          <p>🔒 Secure Admin Access</p>
          <p>Only authorized administrators can access this area.</p>
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', opacity: 0.8 }}>
            Developed by <span style={{ fontWeight: 600 }}>VRND Business Solution</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin; 